import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import i18next from 'i18next';
import { matchPath } from 'react-router-dom';
import { GUID_REGEX_PATTERN } from '@/main/app.constants';
import { getFolderName, getFolderRoot } from '@/utilities/homescreen.utilities';
import { getCurrentTabFromHash } from '@/utilities/utilities';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqWorkbooksApi } from '@seeqdev/api/endpoint/WorkbooksApi';
import type { Params } from 'react-router-dom';
import type { ItemOutputV1 } from '@seeqdev/api/model/ItemOutputV1';

export type PastedWorksheetContext = {
  workbookId: string;
  worksheetId: string;
  projectId: string;
  folderId?: string;
  title: string;
  contentType: string;
  seeqUrl: string;
};

export type SeeqContentReference = {
  workbookId?: string;
  worksheetId?: string;
  projectId?: string;
  folderId?: string;
};

type ParsedSeeqContentLink = {
  contentId: string;
  worksheetId: string;
  allowedContentTypes?: Set<string>;
  rootFolderTitle?: string;
};

const DEFAULT_ALLOWED_CONTENT_TYPES = new Set<string>([
  SeeqNames.Types.Analysis,
  SeeqNames.Types.Topic,
  SeeqNames.Types.Vantage,
  SeeqNames.Types.Project,
]);

const WORKBOOK_CONTENT_TYPES = new Set<string>([
  SeeqNames.Types.Analysis,
  SeeqNames.Types.Topic,
  SeeqNames.Types.Vantage,
]);
const VANTAGE_CONTENT_TYPES = new Set<string>([SeeqNames.Types.Vantage]);
const PROJECT_CONTENT_TYPES = new Set<string>([SeeqNames.Types.Project]);
const FOLDER_CONTENT_TYPES = new Set<string>([SeeqNames.Types.Folder]);
const GUID_REGEX = new RegExp(`^${GUID_REGEX_PATTERN}$`, 'i');

type ContentRoutePattern = {
  path: string;
  end?: boolean;
  getContentLink: (params: Params<string>) => ParsedSeeqContentLink | null;
};

function getWorkbookContentLink(params: Params<string>): ParsedSeeqContentLink | null {
  if (!params.workbookId || !GUID_REGEX.test(params.workbookId)) {
    return null;
  }
  if (params.worksheetId && !GUID_REGEX.test(params.worksheetId)) {
    return null;
  }

  return {
    contentId: params.workbookId,
    worksheetId: params.worksheetId || '',
    allowedContentTypes: WORKBOOK_CONTENT_TYPES,
  };
}

function getProjectContentLink(params: Params<string>): ParsedSeeqContentLink | null {
  return params.projectId && GUID_REGEX.test(params.projectId)
    ? { contentId: params.projectId, worksheetId: '', allowedContentTypes: PROJECT_CONTENT_TYPES }
    : null;
}

function getFolderContentLink(params: Params<string>): ParsedSeeqContentLink | null {
  return params.folderId && GUID_REGEX.test(params.folderId)
    ? { contentId: params.folderId, worksheetId: '', allowedContentTypes: FOLDER_CONTENT_TYPES }
    : null;
}

function getRootFolderContentLink(url: URL): ParsedSeeqContentLink | null {
  if (url.pathname !== '/workbooks') {
    return null;
  }

  const tabHash = url.searchParams.get('t');
  const tab = tabHash ? getCurrentTabFromHash(tabHash) : undefined;
  const folderId = tab ? getFolderRoot(tab) : undefined;
  const folderNameKey = tab ? getFolderName(tab) : undefined;
  return folderId && folderNameKey
    ? {
        contentId: folderId,
        worksheetId: '',
        allowedContentTypes: FOLDER_CONTENT_TYPES,
        rootFolderTitle: i18next.t(folderNameKey),
      }
    : null;
}

const WORKBOOK_ROUTE_PATHS = [
  '/workbook/:workbookId/worksheet/:worksheetId',
  '/workbook/:workbookId/worksheet',
  '/workbook/:workbookId',
  '/:currentFolderId/workbook/:workbookId/worksheet/:worksheetId',
  '/:currentFolderId/workbook/:workbookId/worksheet',
  '/:currentFolderId/workbook/:workbookId',
  '/view/worksheet/:workbookId/:worksheetId',
  '/view/worksheet/:workbookId',
  '/:currentFolderId/view/worksheet/:workbookId/:worksheetId',
  '/:currentFolderId/view/worksheet/:workbookId',
  '/present/worksheet/:workbookId/:worksheetId',
  '/present/worksheet/:workbookId',
  '/present-plus/worksheet/:workbookId/:worksheetId',
  '/present-plus/worksheet/:workbookId',
];

const CONTENT_LINK_PATTERNS: ContentRoutePattern[] = [
  ...WORKBOOK_ROUTE_PATHS.map((path) => ({ path, getContentLink: getWorkbookContentLink })),
  {
    path: '/view/:contentId',
    getContentLink: (params) =>
      params.contentId && GUID_REGEX.test(params.contentId)
        ? { contentId: params.contentId, worksheetId: '', allowedContentTypes: VANTAGE_CONTENT_TYPES }
        : null,
  },
  { path: '/data-lab/:projectId', end: false, getContentLink: getProjectContentLink },
  { path: '/project/:projectId', getContentLink: getProjectContentLink },
  { path: '/:folderId/project/:projectId', getContentLink: getProjectContentLink },
  { path: '/:folderId/folder', getContentLink: getFolderContentLink },
];

function getAllowedContentTypes(allowedTypes?: string[]): Set<string> {
  return allowedTypes ? new Set(allowedTypes) : DEFAULT_ALLOWED_CONTENT_TYPES;
}

function hasAllowedRouteContentType(contentLink: ParsedSeeqContentLink, allowedContentTypes: Set<string>): boolean {
  if (!contentLink.allowedContentTypes) {
    return true;
  }

  return Array.from(contentLink.allowedContentTypes).some((contentType) => allowedContentTypes.has(contentType));
}

function parseSeeqContentLink(url: string, allowedTypes?: string[]): ParsedSeeqContentLink | null {
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(url.trim(), 'https://seeq.example');
  } catch {
    return null;
  }

  const allowedContentTypes = getAllowedContentTypes(allowedTypes);
  const rootFolderLink = getRootFolderContentLink(parsedUrl);
  if (rootFolderLink) {
    return hasAllowedRouteContentType(rootFolderLink, allowedContentTypes) ? rootFolderLink : null;
  }

  const pathname = parsedUrl.pathname;

  for (const { path, end = true, getContentLink } of CONTENT_LINK_PATTERNS) {
    const match = matchPath({ path, end, caseSensitive: false }, pathname);
    if (match) {
      const contentLink = getContentLink(match.params);
      return contentLink && hasAllowedRouteContentType(contentLink, allowedContentTypes) ? contentLink : null;
    }
  }

  return null;
}

async function getContentItem(contentId: string): Promise<ItemOutputV1 | null> {
  try {
    const { data } = await sqItemsApi.getItemAndAllProperties({ id: contentId });
    return data;
  } catch {
    return null;
  }
}

async function isWorksheetInWorkbook(workbookId: string, worksheetId: string): Promise<boolean> {
  try {
    await sqWorkbooksApi.getWorksheet({ workbookId, worksheetId });
    return true;
  } catch {
    return false;
  }
}

function getCanonicalSeeqUrl(contentId: string, contentType: string, worksheetId: string): string {
  if (contentType === SeeqNames.Types.Project) {
    return `/data-lab/${contentId}`;
  }
  if (contentType === SeeqNames.Types.Folder) {
    return `/${contentId}/folder`;
  }
  if (worksheetId) {
    return `/workbook/${contentId}/worksheet/${worksheetId}`;
  }
  return `/workbook/${contentId}`;
}

function getContentIdField(
  contentId: string,
  contentType: string,
): Pick<PastedWorksheetContext, 'folderId' | 'projectId' | 'workbookId'> {
  return {
    workbookId: contentType !== SeeqNames.Types.Project && contentType !== SeeqNames.Types.Folder ? contentId : '',
    projectId: contentType === SeeqNames.Types.Project ? contentId : '',
    ...(contentType === SeeqNames.Types.Folder ? { folderId: contentId } : {}),
  };
}

export function parseSeeqContentReference(url: string, allowedTypes?: string[]): SeeqContentReference | null {
  const contentLink = parseSeeqContentLink(url, allowedTypes);
  if (!contentLink) return null;

  if (contentLink.allowedContentTypes === PROJECT_CONTENT_TYPES) {
    return { projectId: contentLink.contentId };
  }

  if (contentLink.allowedContentTypes === FOLDER_CONTENT_TYPES) {
    return { folderId: contentLink.contentId };
  }

  return {
    workbookId: contentLink.contentId,
    worksheetId: contentLink.worksheetId || undefined,
  };
}

/**
 * Accepts direct Seeq content links for the same content types selectable in Agent Builder.
 */
export function isPastedWorksheetLink(url: string, allowedTypes?: string[]): boolean {
  return parseSeeqContentLink(url, allowedTypes) != null;
}

/**
 * Resolves a pasted Seeq content link into the Seeq node data Agent Builder persists.
 */
export async function resolvePastedWorksheetContext(
  url: string,
  allowedTypes?: string[],
): Promise<PastedWorksheetContext> {
  const allowedContentTypes = getAllowedContentTypes(allowedTypes);
  const contentLink = parseSeeqContentLink(url, allowedTypes);
  if (!contentLink) {
    throw new Error();
  }

  if (contentLink.rootFolderTitle) {
    return {
      workbookId: '',
      worksheetId: '',
      projectId: '',
      folderId: contentLink.contentId,
      title: contentLink.rootFolderTitle,
      contentType: SeeqNames.Types.Folder,
      seeqUrl: getCanonicalSeeqUrl(contentLink.contentId, SeeqNames.Types.Folder, ''),
    };
  }

  const item = await getContentItem(contentLink.contentId);
  const contentType = item?.type || '';
  if (
    !item?.name ||
    !allowedContentTypes.has(contentType) ||
    (contentLink.allowedContentTypes && !contentLink.allowedContentTypes.has(contentType))
  ) {
    throw new Error();
  }

  const contentId = item.id || contentLink.contentId;
  if (contentLink.worksheetId && !(await isWorksheetInWorkbook(contentId, contentLink.worksheetId))) {
    throw new Error();
  }

  return {
    ...getContentIdField(contentId, contentType),
    worksheetId: contentLink.worksheetId,
    title: item.name,
    contentType,
    seeqUrl: getCanonicalSeeqUrl(contentId, contentType, contentLink.worksheetId),
  };
}
